import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import PastMedicalHistory from './PastMedicalHistory';
import Medications from './Medications';
import SuccessModal from './SuccessModal';

const ParticipantInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 12px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 20px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 6px;
  width: 100%;
`;

const FormLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 4px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
`;

const RequiredStar = styled.span`
  color: #E43434;
`;

const FormInput = styled.input`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 18px 16px;
  gap: 10px;
  width: ${props => props.wide ? '280px' : '150px'};
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;

  &::placeholder {
    color: #A7A7AA;
  }
`;

const FormSelect = styled.select`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 18px 16px;
  gap: 10px;
  width: 250px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;

  &::placeholder {
    color: #A7A7AA;
  }
`;

const SeverityRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SeverityList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const RemoveSeverityButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #EF8585;
  border-radius: 4px;
  background: none;
  color: #EF8585;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
`;

const AddLabelButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 16px;
  gap: 8px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
  cursor: pointer;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 32px;
  width: 500px;
`;

const RadioLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
`;

const FitzpatrickContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const FitzpatrickOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const FitzpatrickOption = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 8px;
  gap: 8px;
  cursor: pointer;
  border-radius: 12px;
  border: 2px solid ${props => props.checked ? '#2575BD' : 'transparent'};
  background: ${props => props.checked ? '#f0f8ff' : 'transparent'};
  transition: all 0.2s ease;
  min-width: 90px;
  min-height: 120px;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const FitzpatrickColorCircle = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.textColor || '#000'};
  font-weight: 700;
  font-size: 16px;
  flex-shrink: 0;
`;

const FitzpatrickRadio = styled.input`
  display: none;
`;

const FitzpatrickDescription = styled.span`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 11px;
  text-align: center;
  color: #6D6D71;
  width: 85px;
  line-height: 1.3;
  display: block;
  margin-top: 2px;
`;

const SaveButton = styled.button`
  display: flex;
  width: 230px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 15px 15px;
  gap: 12px;
  background: #2575BD;
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #FFFFFF;
  cursor: pointer;
  margin-top: 20px;

  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;

const Save = styled.button`
  width: 180px;
  height: 54px;
  background-color: ${props => props.disabled ? '#E0F0FF' : '#2575BD'};
  color: ${props => props.disabled ? '#91D3FF' : '#FFFFFF'};
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 18px;
  line-height: 21px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.3s;
  margin: 0 auto;
  display: block;

  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 400px;
  width: 100%;
`;

const ModalTitle = styled.h2`
  margin-top: 0;
`;

const ModalButton = styled.button`
  background-color: #2575BD;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
`;



// Ordered by valve: aortic, mitral, pulmonic, tricuspid — the full 4 valves x
// { regurgitation, stenosis } matrix the backend's study config already defines.
const PATHOLOGY_OPTIONS = ['AR', 'AS', 'MR', 'MS', 'PR', 'TR', 'TS'];
const SEVERITY_OPTIONS = ['Trivial', 'Mild', 'Mild to Moderate', 'Moderate', 'Moderate to Severe', 'Severe'];

// Build the per-row echo labels (each = { pathology, severity }) from stored
// clinical_assessment data, migrating older single-severity / pathology-list shapes.
const migrateEchoLabels = (ca = {}) => {
  if (Array.isArray(ca.echo_labels)) return ca.echo_labels;
  const severity = Array.isArray(ca.severities)
    ? (ca.severities[0] || '')
    : (ca.severity || '');
  const pathologies = Array.isArray(ca.pathologies)
    ? ca.pathologies.filter((p) => p && p !== 'None')
    : [];
  if (pathologies.length > 0) {
    return pathologies.map((pathology) => ({ pathology, severity }));
  }
  if (severity && severity !== 'None') {
    return [{ pathology: '', severity }];
  }
  return [];
};

const ParticipantInfo = ({ participantId, formData, onFormDataChange, onSaveSuccess }) => {
  const [data, setData] = useState({
    demographics: {
      age: '',
      sex: '',
      smoking_status: '',
      fitzpatrick_scale: ''
    },
    clinical_assessment: {
      is_healthy: '',
      echo_labels: []
    },
    past_medical_history: [],
    medications: []
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fitzpatrick Scale options with colors and descriptions
  const fitzpatrickOptions = [
    {
      value: '1',
      color: '#fdbcb4',
      textColor: '#000',
      description: 'Always burns, never tans'
    },
    {
      value: '2', 
      color: '#f1a688',
      textColor: '#000',
      description: 'Usually burns, tans minimally'
    },
    {
      value: '3',
      color: '#deb887',
      textColor: '#000', 
      description: 'Sometimes burns, tans gradually'
    },
    {
      value: '4',
      color: '#c19a6b',
      textColor: '#000',
      description: 'Burns minimally, always tans'
    },
    {
      value: '5',
      color: '#a0522d',
      textColor: '#fff',
      description: 'Rarely burns, tans profusely'
    },
    {
      value: '6',
      color: '#8b4513',
      textColor: '#fff', 
      description: 'Never burns, deeply pigmented'
    }
  ];

  useEffect(() => {
    const fetchParticipantInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}participant/get-participant-info?participant_id=${participantId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          const apiData = response.data;
          const initialData = {
            demographics: {
              age: apiData.demographics?.age || '',
              sex: apiData.demographics?.sex?.toLowerCase() || '',
              smoking_status: mapSmokingStatus(apiData.demographics?.smoking_status) || '',
              fitzpatrick_scale: apiData.demographics?.fitzpatrick_scale || ''
            },
            clinical_assessment: {
              is_healthy: apiData.clinical_assessment?.is_healthy ?? '',
              echo_labels: migrateEchoLabels(apiData.clinical_assessment)
            },
            past_medical_history: apiData.past_medical_history || [],
            medications: apiData.medications || []
          };

          setData(initialData);
          onFormDataChange(initialData);
        }
      } catch (error) {
        console.error('Error fetching participant info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchParticipantInfo();
  }, [participantId]);

  const mapSmokingStatus = (status) => {
    switch (status?.toLowerCase()) {
      case 'never smoker':
      case 'never-smoked':
        return 'never-smoked';
      case 'former smoker':
      case 'current smoker':
      case 'occasional smoker':
      case 'sometimes-smoked':
        return 'sometimes-smoked';
      default:
        return '';
    }
  };

  const handleInputChange = (section, field, value) => {
    const updatedData = {
      ...data,
      [section]: {
        ...data[section],
        [field]: value
      }
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const handleHealthyChange = (isHealthy) => {
    const updatedData = {
      ...data,
      clinical_assessment: { is_healthy: isHealthy, echo_labels: [] }
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const updateClinicalAssessment = (patch) => {
    const updatedData = {
      ...data,
      clinical_assessment: { ...data.clinical_assessment, ...patch }
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  // The UI always shows at least one echo label row; an empty stored array is
  // treated as a single blank { pathology, severity } entry at index 0.
  const getEchoLabels = () => {
    const current = data.clinical_assessment.echo_labels || [];
    return current.length > 0 ? current : [{ pathology: '', severity: '' }];
  };

  const updateEchoLabel = (index, field, value) => {
    const next = getEchoLabels().map((label, i) =>
      i === index ? { ...label, [field]: value } : label
    );
    updateClinicalAssessment({ echo_labels: next });
  };

  const handleAddEchoLabel = () => {
    updateClinicalAssessment({
      echo_labels: [...getEchoLabels(), { pathology: '', severity: '' }]
    });
  };

  const handleRemoveEchoLabel = (index) => {
    const next = getEchoLabels().filter((_, i) => i !== index);
    updateClinicalAssessment({ echo_labels: next });
  };

  const handlePastMedicalHistoryChange = (newHistory) => {
    const updatedData = {
      ...data,
      past_medical_history: newHistory
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const handleMedicationsChange = (newMedications) => {
    const updatedData = {
      ...data,
      medications: newMedications
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Filter out empty/invalid entries from past_medical_history
      const filteredPastMedicalHistory = data.past_medical_history.filter(
        history => history.condition && history.condition.trim() !== ''
      );
      
      // Filter out empty/invalid entries from medications
      const filteredMedications = data.medications.filter(
        medication => medication && medication.trim() !== ''
      );

      // Filter out blank echocardiogram labels (no valve and no severity chosen)
      const filteredEchoLabels = (data.clinical_assessment.echo_labels || []).filter(
        label => (label.pathology && label.pathology.trim() !== '') ||
                 (label.severity && label.severity.trim() !== '')
      );

      // Add hardcoded ethnicity as 'Indian' when sending to backend
      const dataToSend = {
        ...data,
        demographics: {
          ...data.demographics,
          ethnicity: 'Indian'
        },
        clinical_assessment: {
          ...data.clinical_assessment,
          echo_labels: filteredEchoLabels
        },
        past_medical_history: filteredPastMedicalHistory,
        medications: filteredMedications
      };
      
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}participant/update-info`,
        { participant_id: participantId, ...dataToSend },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccessMessage(`Participant Info successfully saved for Participant ID: ${participantId}`);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error saving participant info:', error);
      // TODO: Implement error handling
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    onSaveSuccess();
  };

  if (isLoading) {
    return <div>Loading participant info...</div>;
  }

  return (
    <ParticipantInfoContainer>
      <SectionContainer>
        <SectionTitle>DEMOGRAPHICS</SectionTitle>
        <ContentBox>
          <FormField>
            <FormLabel>
              <RequiredStar>*</RequiredStar>
              Age (in years)
            </FormLabel>
            <FormInput 
              type="number" 
              placeholder="Enter age" 
              min="0" 
              value={data.demographics.age}
              onChange={(e) => handleInputChange('demographics', 'age', e.target.value)}
            />
          </FormField>
          
          <FormField>
            <FormLabel>
              <RequiredStar>*</RequiredStar>
              Sex
            </FormLabel>
            <RadioGroup>
              {['male', 'female'].map((gender) => (
                <RadioLabel key={gender}>
                  <RadioInput 
                    type="radio" 
                    name="sex" 
                    value={gender}
                    checked={data.demographics.sex?.toLowerCase() === gender}
                    onChange={() => handleInputChange('demographics', 'sex', gender)}
                  />
                  {gender.charAt(0).toUpperCase() + gender.slice(1)}
                </RadioLabel>
              ))}
            </RadioGroup>
          </FormField>
          
          <FormField>
            <FormLabel>
              Fitzpatrick Scale
            </FormLabel>
            <FitzpatrickContainer>
              <FitzpatrickOptions>
                {fitzpatrickOptions.map((option) => (
                  <FitzpatrickOption 
                    key={option.value}
                    checked={data.demographics.fitzpatrick_scale === option.value}
                  >
                    <FitzpatrickRadio
                      type="radio"
                      name="fitzpatrick_scale"
                      value={option.value}
                      checked={data.demographics.fitzpatrick_scale === option.value}
                      onChange={() => handleInputChange('demographics', 'fitzpatrick_scale', option.value)}
                    />
                    <FitzpatrickColorCircle 
                      color={option.color}
                      textColor={option.textColor}
                    >
                      {option.value}
                    </FitzpatrickColorCircle>
                    <FitzpatrickDescription>
                      {option.description}
                    </FitzpatrickDescription>
                  </FitzpatrickOption>
                ))}
              </FitzpatrickOptions>
            </FitzpatrickContainer>
          </FormField>
          
          <FormField>
            <FormLabel>
              <RequiredStar>*</RequiredStar>
              Smoking Status
            </FormLabel>
            <RadioGroup>
              {['never-smoked', 'sometimes-smoked'].map((status) => (
                <RadioLabel key={status}>
                  <RadioInput 
                    type="radio" 
                    name="smoking" 
                    value={status}
                    checked={data.demographics.smoking_status === status}
                    onChange={() => handleInputChange('demographics', 'smoking_status', status)}
                  />
                  {status === 'never-smoked' ? 'Never smoked' : 'Sometimes smoked'}
                </RadioLabel>
              ))}
            </RadioGroup>
          </FormField>
        </ContentBox>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>CLINICAL ASSESSMENT</SectionTitle>
        <ContentBox>
          <FormField>
            <FormLabel>
              <RequiredStar>*</RequiredStar>
              Healthy Participant?
            </FormLabel>
            <RadioGroup>
              <RadioLabel>
                <RadioInput
                  type="radio"
                  name="is_healthy"
                  checked={data.clinical_assessment.is_healthy === true}
                  onChange={() => handleHealthyChange(true)}
                />
                Yes
              </RadioLabel>
              <RadioLabel>
                <RadioInput
                  type="radio"
                  name="is_healthy"
                  checked={data.clinical_assessment.is_healthy === false}
                  onChange={() => handleHealthyChange(false)}
                />
                No
              </RadioLabel>
            </RadioGroup>
          </FormField>

          {data.clinical_assessment.is_healthy === false && (
            <FormField>
              <FormLabel>
                Label from Echocardiogram (Severity)
              </FormLabel>
              <SeverityList>
                {getEchoLabels().map((label, index) => (
                  <SeverityRow key={index}>
                    <FormSelect
                      value={label.pathology || ''}
                      onChange={(e) => updateEchoLabel(index, 'pathology', e.target.value)}
                    >
                      <option value="">Valve</option>
                      {PATHOLOGY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </FormSelect>
                    <FormSelect
                      value={label.severity || ''}
                      onChange={(e) => updateEchoLabel(index, 'severity', e.target.value)}
                    >
                      <option value="">Severity</option>
                      {SEVERITY_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </FormSelect>
                    {getEchoLabels().length > 1 && (
                      <RemoveSeverityButton
                        type="button"
                        aria-label="Remove label"
                        onClick={() => handleRemoveEchoLabel(index)}
                      >
                        ×
                      </RemoveSeverityButton>
                    )}
                  </SeverityRow>
                ))}
                <AddLabelButton type="button" onClick={handleAddEchoLabel}>
                  + Label from Echocardiogram (Severity)
                </AddLabelButton>
              </SeverityList>
            </FormField>
          )}
        </ContentBox>
      </SectionContainer>

      <PastMedicalHistory
        pastMedicalHistory={data.past_medical_history}
        setPastMedicalHistory={(newHistory) => {
          const updatedData = {
            ...data,
            past_medical_history: newHistory
          };
          setData(updatedData);
          onFormDataChange(updatedData);
        }}
      />

      <Medications
        key={data.medications.length}
        medications={data.medications}
        setMedications={(newMedications) => {
          const updatedMedications = [...newMedications];
          const updatedData = {
            ...data,
            medications: updatedMedications
          };
          setData(updatedData);
          onFormDataChange(updatedData);
        }}
      />
      
      <SaveButton onClick={handleSave}>Save and Next</SaveButton>

      {isModalOpen && (
        <SuccessModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title="Success"
          message={
            <p>
              Participant Info successfully saved for Participant ID: {participantId}
            </p>
          }
        />
      )}
    </ParticipantInfoContainer>
  );
};

export default ParticipantInfo;
